// ---------------------------------------------------------------------------
// The optimizer an exploration runs
//
// The exploration config names it in optimization.algorithms[0], a one-key
// object whose key is the algorithm (see the algorithms package's
// utilities/optimization/factory.py). The New exploration page offers three:
//
//   genetic_algorithm           the genetic algorithm (the presets' default)
//   ga_prescreen                the genetic algorithm with a surrogate model
//                               choosing which of the bred offspring to mark
//                               (genetic_algorithm.surrogate_prescreen)
//   surrogate_coverage_search   a surrogate model of colour and usability
//                               picks every patch to cover new colours
//
// Choosing one writes its settings into the document: the ones it held
// before when the user switches back, else the algorithm's defaults, so
// every setting is a visible field on the Optimization card.
// ---------------------------------------------------------------------------

export const OPTIMIZERS = [
  {
    key: "genetic_algorithm",
    label: "Genetic algorithm",
    description: "Breeds each generation from the best-ranked patches of the last one.",
  },
  {
    key: "ga_prescreen",
    label: "GA + pre-screen",
    description:
      "The genetic algorithm breeds several times a generation; a model of every measured patch picks the offspring most likely to add new colours.",
  },
  {
    key: "surrogate_coverage_search",
    label: "Surrogate search",
    description:
      "A model of every measured patch predicts colour and usability; each generation goes to the parameter sets most likely to reach colours not covered yet.",
  },
];

// The algorithms package's defaults (genetic_algorithm in the presets,
// surrogate_coverage/prescreen.py PRESCREEN_DEFAULTS, surrogate_coverage/
// optimizer.py DEFAULTS). Settings whose default is "none" (seed, maximum
// usable width) are left out: their fields may stay empty.
export const GA_DEFAULTS = { crossing_probability: 0.7, initial_population: null };
export const PRESCREEN_DEFAULTS = {
  oversampling: 10,
  cell_edge_delta_e: 5.0,
  max_fit_points: 400,
  minimum_usable_for_model: 10,
  colour_scale_delta_e: 20.0,
  noise_floor_delta_e: 2.0,
};
export const SURROGATE_DEFAULTS = {
  cell_edge_delta_e: 5.0,
  acquisition: "combined",
  hull_exterior_share: 0.25,
  hull_exterior_min_p_usable: 0.7,
  batch_rule: "thompson",
  sigma_floor_delta_e: 1.0,
  replicate_slots: 5,
  max_measurements_per_design: 3,
  candidate_count: 5000,
  local_candidates_per_vertex: 10,
  local_sigma: 0.05,
  thompson_pool_size: 1500,
  use_usability_model: true,
  max_fit_points: 400,
  colour_scale_delta_e: 20.0,
  noise_floor_delta_e: 2.0,
  minimum_usable_for_model: 10,
};

function firstAlgorithm(config) {
  const algorithms = config?.optimization?.algorithms;
  if (!Array.isArray(algorithms) || algorithms.length === 0) return null;
  const block = algorithms[0];
  if (!block || typeof block !== "object") return null;
  const names = Object.keys(block);
  return names.length === 1 ? { name: names[0], settings: block[names[0]] } : null;
}

/** The algorithm key of optimization.algorithms[0], or null. */
export function algorithmName(config) {
  const first = firstAlgorithm(config);
  return first ? first.name : null;
}

/** The OPTIMIZERS key the config selects, or null when it names none of them. */
export function optimizerOf(config) {
  const first = firstAlgorithm(config);
  if (!first) return null;
  if (first.name === "genetic_algorithm") {
    return first.settings && first.settings.surrogate_prescreen ? "ga_prescreen" : "genetic_algorithm";
  }
  return OPTIMIZERS.some((option) => option.key === first.name) ? first.name : null;
}

/**
 * The settings the config holds per optimizer: `ga` (the genetic algorithm's
 * block without the pre-screen), `prescreen` and `surrogate`; absent ones are
 * left out. Kept by the card so switching back restores them.
 */
export function algorithmBlocks(config) {
  const first = firstAlgorithm(config);
  if (!first) return {};
  if (first.name === "genetic_algorithm" && first.settings) {
    const { surrogate_prescreen: prescreen, ...ga } = first.settings;
    return prescreen ? { ga, prescreen } : { ga };
  }
  if (first.name === "surrogate_coverage_search") return { surrogate: first.settings || {} };
  return {};
}

/**
 * A copy of `config` running `key`, with the settings in `remembered`
 * (algorithmBlocks of earlier documents) or the defaults.
 */
export function withOptimizer(config, key, remembered = {}) {
  if (!config || typeof config !== "object" || !config.optimization) return config;
  const ga = remembered.ga || GA_DEFAULTS;
  let block;
  if (key === "genetic_algorithm") block = { genetic_algorithm: { ...ga } };
  else if (key === "ga_prescreen")
    block = { genetic_algorithm: { ...ga, surrogate_prescreen: { ...(remembered.prescreen || PRESCREEN_DEFAULTS) } } };
  else if (key === "surrogate_coverage_search")
    block = { surrogate_coverage_search: { ...(remembered.surrogate || SURROGATE_DEFAULTS) } };
  else return config;
  return { ...config, optimization: { ...config.optimization, algorithms: [block] } };
}

export function optimizerLabel(key) {
  const option = OPTIMIZERS.find((item) => item.key === key);
  return option ? option.label : "Other optimizer";
}
